/**
 * Busca dos perfis de radiossonda.
 *
 * Tudo vem do cache publicado (`AmaView-dados`): o servidor do Wyoming não
 * manda cabeçalho de CORS, então o navegador não fala com ele — só o pipeline.
 * O link para o Skew-T abre em nova aba, que não passa por CORS.
 */
import { fetchWithRetry } from '../lib/fetchRetry';
import { lonLatToMap } from '../map/projection';
import type { DadosSondagem, Indices, Nivel, Perfil, PontoTrilha } from './types';

export const BASE_SONDAGEM =
  (import.meta.env.VITE_DADOS_URL?.replace(/\/cemaden$/, '') ?? 'https://helvecioneto.github.io/AmaView-dados') +
  '/sondagem';

export const WYOMING_ATTRIBUTION =
  'Sondagens: <a href="https://weather.uwyo.edu/upperair/" target="_blank" rel="noopener">University of Wyoming</a>';

function json(url: string, signal?: AbortSignal): Promise<unknown> {
  return fetchWithRetry<unknown>(url, {}, { signal, read: (r) => r.json() as Promise<unknown> });
}

export async function carregarSondagens(signal?: AbortSignal): Promise<DadosSondagem> {
  const [cruEst, cruPer] = await Promise.all([
    json(`${BASE_SONDAGEM}/estacoes.json`, signal),
    json(`${BASE_SONDAGEM}/perfis.json`, signal),
  ]);

  const e = cruEst as { estacoes?: { w: number; n: string; u: string; y: number; x: number }[]; gerado?: string };
  if (!Array.isArray(e.estacoes)) throw new Error('estacoes.json sem a lista `estacoes`');

  const estacoes = e.estacoes
    .filter((x) => Number.isFinite(x.y) && Number.isFinite(x.x))
    .map((x) => {
      const [mx, my] = lonLatToMap(x.x, x.y);
      return { wmo: Number(x.w), nome: String(x.n ?? ''), uf: String(x.u ?? ''), lat: x.y, lon: x.x, mx, my };
    })
    // Pontos além do limbo da Terra não têm coordenada no mapa do GOES.
    .filter((x) => Number.isFinite(x.mx) && Number.isFinite(x.my));

  const p = cruPer as { perfis?: unknown[]; gerado?: string };
  const perfisDe = new Map<number, Perfil[]>();
  for (const cru of Array.isArray(p.perfis) ? p.perfis : []) {
    const perfil = lerPerfil(cru);
    if (!perfil) continue;
    const lista = perfisDe.get(perfil.wmo) ?? [];
    lista.push(perfil);
    perfisDe.set(perfil.wmo, lista);
  }
  // Do mais recente para o mais antigo; sem `ms` vai para o fim.
  for (const lista of perfisDe.values()) {
    lista.sort((a, b) => (b.ms ?? -Infinity) - (a.ms ?? -Infinity));
  }

  const gerado = Date.parse(String(e.gerado ?? ''));
  return { estacoes, perfisDe, gerado: Number.isFinite(gerado) ? gerado : null };
}

/** Valida um perfil publicado. Formato inesperado vira `null`, não exceção. */
function lerPerfil(cru: unknown): Perfil | null {
  const o = cru as Partial<Perfil> & { niveis?: unknown; indices?: unknown };
  if (!Number.isFinite(o.wmo)) return null;

  const niveis: Nivel[] = (Array.isArray(o.niveis) ? o.niveis : [])
    .map((n) => n as Partial<Nivel>)
    .filter((n) => Number.isFinite(n.p) && Number.isFinite(n.z))
    .map((n) => ({
      p: Number(n.p),
      z: Number(n.z),
      t: Number.isFinite(n.t) ? Number(n.t) : null,
      d: Number.isFinite(n.d) ? Number(n.d) : null,
      w: Number.isFinite(n.w) && Number.isFinite(n.s) ? Number(n.w) : null,
      s: Number.isFinite(n.w) && Number.isFinite(n.s) && Number(n.s) >= 0 ? Number(n.s) : null,
    }));

  const trilhaCrua = (o as { trilha?: unknown }).trilha;
  const trilha: PontoTrilha[] | null = Array.isArray(trilhaCrua)
    ? trilhaCrua
        .map((n) => n as Partial<PontoTrilha>)
        .filter((n) => Number.isFinite(n.y) && Number.isFinite(n.x) && Number.isFinite(n.z))
        .map((n) => ({
          y: Number(n.y),
          x: Number(n.x),
          z: Number(n.z),
          t: Number.isFinite(n.t) ? Number(n.t) : null,
        }))
    : null;

  return {
    wmo: Number(o.wmo),
    ms: Number.isFinite(o.ms) ? Number(o.ms) : null,
    niveis,
    indices: (o.indices ?? {}) as Indices,
    trilha: trilha && trilha.length >= 2 ? trilha : null,
    erro: typeof o.erro === 'string' ? o.erro : null,
  };
}

/** Skew-T completo no Wyoming, para quem quiser o diagrama de verdade. */
export function urlSkewT(wmo: number, ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  const dt = `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:00:00`;
  const q = new URLSearchParams({ datetime: dt, id: String(wmo), src: 'UNKNOWN', type: 'PNG:SKEWT' });
  return `https://weather.uwyo.edu/wsgi/sounding?${q}`;
}
